import { useStore } from '../state/zustand-store';
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';

import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';

interface NotificationPopoutProps {
  open: boolean;
  handleClose: () => void;
  anchorEl: HTMLElement | null;
}

function formatDateAgo(date: number) {
  const now = dayjs();
  const then = dayjs(date);
  const diff = now.diff(then, 'second');

  if (diff < 60) {
    return `just now`;
  } else if (diff < 3600) {
    return `${Math.floor(diff / 60)} minutes ago`;
  } else if (diff < 86400) {
    return `${Math.floor(diff / 3600)} hours ago`;
  } else {
    return `${Math.floor(diff / 86400)} days ago`;
  }
}

export default function NotificationPopout({
  open,
  handleClose,
  anchorEl,
}: NotificationPopoutProps) {
  const { notifications, setNotifications } = useStore();
  const hasMarkedAsRead = useRef(false);

  useEffect(() => {
    if (open && !hasMarkedAsRead.current) {
      const updatedNotifications = notifications.map((notification) => ({
        ...notification,
        read: true,
      }));
      setNotifications(updatedNotifications);
      hasMarkedAsRead.current = true;
    } else if (!open) {
      hasMarkedAsRead.current = false;
    }
  }, [open, notifications, setNotifications]);

  return (
    <Menu
      id="notification-menu"
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: -60,
        horizontal: 'right',
      }}
      open={open}
      onClose={handleClose}
    >
      <Stack direction="row" sx={{ p: 1 }} justifyContent="space-between">
        <Typography>Notifications</Typography>
        {notifications.length > 0 && (
          <Chip
            onClick={() => {
              setNotifications([]);
            }}
            label="Clear All"
            size="small"
          />
        )}
      </Stack>
      <Divider />

      <DialogContent
        sx={{
          width: 500,
          maxHeight: 200,
          overflowY: 'auto',
          p: 0.5,
        }}
      >
        <Stack gap={1}>
          {notifications.length > 0 ? (
            notifications.map((notification) => (
              <Alert
                severity={
                  notification.severity as
                    | 'error'
                    | 'warning'
                    | 'info'
                    | 'success'
                }
                key={notification.id}
                onClose={() => {
                  setNotifications(
                    notifications.filter((n) => n.id !== notification.id)
                  );
                }}
                icon={false}
              >
                <Stack>
                  <Typography variant="body2">
                    {notification.message}
                  </Typography>

                  <Stack direction="row" gap={1}>
                    <Typography variant="caption">
                      {formatDateAgo(notification.date)}
                    </Typography>
                    {notification.docLink && (
                      <>
                        <Divider orientation="vertical" flexItem />
                        <Link
                          component="a"
                          href={`https://docs.google.com/spreadsheets/d/${notification.docLink}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="caption"
                        >
                          View Report
                        </Link>
                      </>
                    )}
                  </Stack>
                </Stack>
              </Alert>
            ))
          ) : (
            <Typography sx={{ mx: 'auto', p: 1 }}>
              No new notifications
            </Typography>
          )}
        </Stack>
      </DialogContent>
    </Menu>
  );
}
